import { useState, useRef } from 'react';
import { FileText, Check, CheckCircle2 } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/client';
import { toast } from '@/state/toast';

interface Props {
  projectId: string;
  snapshotId: string | null;
  onClose: () => void;
}

type Step = 1 | 2 | 3;

interface ImportPreviewRow {
  num: number;
  section: string;
  section_code?: string;
  severity: string;
  text: string;
  category?: string;
}

const SEVERITY_RU: Record<string, string> = {
  critical: 'Критическое',
  high: 'Высокое',
  medium: 'Среднее',
  low: 'Низкое',
};

export function WordImportModal({ projectId, snapshotId, onClose }: Props) {
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>(1);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreviewRow[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set()); // индексы в preview[]
  const fileRef = useRef<HTMLInputElement>(null);

  const [previewLoading, setPreviewLoading] = useState(false);

  const importMut = useMutation({
    mutationFn: async () => {
      if (!file || !snapshotId) throw new Error('Файл или редакция не выбраны');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('snapshot_id', snapshotId);
      // Используем file-endpoint: бэк сам парсит и импортирует
      const res = await fetch(`/projects/${projectId}/import/remarks-docx-file`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        let detail: unknown;
        try { detail = await res.json(); } catch {}
        throw new ApiError(res.status, `HTTP ${res.status}`, detail);
      }
      return res.json() as Promise<{ imported?: number; created?: string[]; matched?: string[] }>;
    },
    onSuccess: (data) => {
      const total = (data.created?.length ?? 0) + (data.matched?.length ?? 0);
      toast.success(`Импортировано ${data.imported ?? total} замечаний`);
      qc.invalidateQueries({ queryKey: ['remarks', projectId] });
      setStep(3);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось импортировать'),
  });

  const onFile = async (f: File) => {
    setFile(f);
    setPreviewLoading(true);
    setStep(2);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await fetch(`/projects/${projectId}/import/remarks-docx/preview`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        let detail = '';
        try { detail = JSON.stringify(await res.json()); } catch {}
        throw new Error(`Ошибка разбора файла: HTTP ${res.status} ${detail}`);
      }
      const data = await res.json() as { remarks: Array<{ num: number; section: string; section_code: string; severity: string; text: string; category?: string }> };
      const rows: ImportPreviewRow[] = data.remarks.map(r => ({
        num: r.num,
        section: r.section_code || r.section,
        section_code: r.section_code,
        severity: SEVERITY_RU[r.severity] || r.severity,
        text: r.text,
        category: r.category,
      }));
      setPreview(rows);
      setSelected(new Set(rows.map((_, i) => i)));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось разобрать файл');
      setStep(1);
      setFile(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggle = (n: number) => {
    const next = new Set(selected);
    next.has(n) ? next.delete(n) : next.add(n);
    setSelected(next);
  };

  const handleImport = () => {
    if (!snapshotId) {
      toast.error('Нет активного редакции — загрузите редакцию в табе Документы');
      return;
    }
    importMut.mutate();
  };

  let footer: React.ReactNode = null;
  if (step === 1) {
    footer = (
      <>
        <Button variant="ghost" onClick={onClose}>Отмена</Button>
        <Button variant="primary" disabled>Далее</Button>
      </>
    );
  } else if (step === 2) {
    footer = (
      <>
        <Button variant="ghost" onClick={() => setStep(1)}>← Назад</Button>
        <Button variant="primary" onClick={handleImport} disabled={selected.size === 0 || importMut.isPending}>
          {importMut.isPending ? 'Импорт…' : `Импортировать ${selected.size} замечаний`}
        </Button>
      </>
    );
  } else {
    footer = <Button variant="primary" onClick={onClose} style={{ width: '100%' }}>Закрыть</Button>;
  }

  return (
    <Modal title="Импорт замечаний из Word" onClose={onClose} maxWidth={600} footer={footer}>
      <StepIndicator current={step} />

      {step === 1 && (
        <div className="mt-6">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full rounded-card text-center"
            style={{ padding: 32, background: '#F3F4F6', border: '2px dashed #E5E7EB', cursor: 'pointer' }}
          >
            <FileText size={40} stroke="#6B7280" style={{ margin: '0 auto' }} />
            <div className="text-ink mt-3" style={{ fontSize: 15, fontWeight: 500 }}>Перетащите .docx или нажмите для выбора</div>
            <div className="text-muted mt-1" style={{ fontSize: 11 }}>Поддерживается формат «Реестр замечаний»</div>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".docx"
            onChange={(e) => { if (e.target.files?.[0]) onFile(e.target.files[0]); }}
            style={{ display: 'none' }}
          />
          {/* Кнопка «Скачать шаблон» убрана: реального файла шаблона нет.
              Импорт работает с любой таблицей замечаний госэкспертизы (формат
              «№ п/п | Замечание | Ответ | Лист | Рассмотрение»). */}
        </div>
      )}

      {step === 2 && (
        <div className="mt-6">
          {previewLoading ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#64748B' }}>
              Анализируем файл…
            </div>
          ) : (
            <div className="rounded-card" style={{ padding: 16, border: '1px solid #E5E7EB', maxHeight: 480, overflowY: 'auto' }}>
              <div className="font-heading text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
                Распознано {preview.length} замечаний
              </div>
              {file && <div className="text-muted mt-1" style={{ fontSize: 11 }}>{file.name} · {(file.size / 1024).toFixed(0)} КБ</div>}
              <div className="mt-3">
                {preview.map((r, idx) => (
                  <label
                    key={`${r.section_code}-${r.num}-${idx}`}
                    className="flex items-start"
                    style={{ padding: '10px 0', borderBottom: '1px solid #E5E7EB', gap: 10, cursor: 'pointer' }}
                  >
                    <input type="checkbox" checked={selected.has(idx)} onChange={() => toggle(idx)} style={{ marginTop: 4, accentColor: '#F59E0B' }} />
                    <div style={{ flex: 1 }}>
                      <div className="text-muted" style={{ fontSize: 11 }}>№{r.num} · {r.section} · {r.severity}</div>
                      <div className="text-ink mt-0.5" style={{ fontSize: 13 }}>{r.text}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="text-center" style={{ padding: '32px 0' }}>
          <CheckCircle2 size={56} stroke="#10B981" style={{ margin: '0 auto' }} />
          <h4 className="font-heading text-ink mt-4" style={{ fontSize: 20, fontWeight: 600 }}>Готово</h4>
          <p className="text-muted mt-2" style={{ fontSize: 13 }}>
            Импортировано {selected.size} замечаний в проект {projectId.slice(0, 8)}
          </p>
        </div>
      )}
    </Modal>
  );
}

function StepIndicator({ current }: { current: Step }) {
  return (
    <div className="flex items-center" style={{ gap: 12 }}>
      {[1, 2, 3].map((n) => {
        const done = current > n;
        const active = current === n;
        return (
          <div key={n} className="flex items-center" style={{ gap: 12, flex: n < 3 ? 1 : 0 }}>
            <div className="flex items-center" style={{ gap: 8 }}>
              <span
                className="rounded-pill flex items-center justify-center tabular"
                style={{
                  width: 28, height: 28,
                  background: done ? '#10B981' : active ? '#F59E0B' : '#F3F4F6',
                  color: (done || active) ? '#FFFFFF' : '#6B7280',
                  fontWeight: 600, fontSize: 13,
                }}
              >{done ? <Check size={14} stroke="#FFFFFF" /> : n}</span>
              <span style={{ fontSize: 13, fontWeight: active ? 600 : 500, color: active ? '#0A1628' : '#6B7280' }}>
                {n === 1 ? 'Файл' : n === 2 ? 'Просмотр' : 'Импорт'}
              </span>
            </div>
            {n < 3 && <span style={{ flex: 1, height: 1, background: '#E5E7EB' }} />}
          </div>
        );
      })}
    </div>
  );
}
